import { Fragment, useEffect, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import editProfileUserPc from "assets/images/others/host-side-images/editProfileUSer.png";
import { ReactComponent as EditPic } from "assets/images/svgs/edit-profiles.svg";
import { ReactComponent as InforCircle } from "assets/images/svgs/info-circle.svg";
import { useNavigate } from "react-router-dom";
import businsessProfileBg from "assets/images/others/host-side-images/businessBg.png";
import Breadcrumb, { BreadcrumbLink } from "components/breadcrumbs";
import PhoneNumberInput from "components/phone-number-input";
import { siteRoutes } from "utils/helpers/enums/routes";
import { HostProfileDTO } from "utils/helpers/models/host-dtos/host-profile.dto";
import useHook from "./useHooks";
import { useSelector } from "react-redux";
import useStore from "hooks/useStore";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import { warningToaster } from "utils/helpers/common/alert-service";
import statesData from "components/dummyData/states.json";
import Loader from "components/loader";
import { GENDERS, INDUSTRY_TYPES } from "utils/helpers/enums/shared.enum";

const statesList = statesData.states;

const EditProfile: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"personal" | "business">(
    "personal"
  );

  const [formData, setFormData] = useState<HostProfileDTO>(
    new HostProfileDTO()
  );
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    control,
    watch,
  } = useForm<HostProfileDTO>({ defaultValues: formData });
  const { setRegisteredData } = useStore();
  const [cityList, setCityList] = useState<string[]>([]);
  const [isMissionTooltipVisible, setIsMissionTooltipVisible] = useState(false);
  const [isAboutTooltipVisible, setIsAboutTooltipVisible] = useState(false);
  const [isBusinessPicLoading, setIsBusinessPicLoading] = useState(true);

  const {
    uploadFile,
    updateProfile,
    updateBusinessProfile,
    getHostProfileData,
    hostProfile,
  } = useHook();

  const registeredData = useSelector(
    (state: any) => state.sharedReducer.registeredData
  );

  useEffect(() => {
    if (activeTab === "business") {
      setIsBusinessPicLoading(true);
      const timer = setTimeout(() => {
        setIsBusinessPicLoading(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [activeTab]);

  useEffect(() => {
    if (registeredData) {
      populateFormData();
    }
  }, [registeredData]);

  // Watch phone number fields to sync with formData
  const phoneValue = watch("phone");
  const businessPhoneValue = watch("businessPhoneNumber");

  // Sync phone numbers from react-hook-form to formData
  useEffect(() => {
    if (phoneValue !== formData.phone) {
      setFormData((prev) => ({ ...prev, phone: phoneValue || "" }));
    }
  }, [phoneValue]);

  useEffect(() => {
    if (businessPhoneValue !== formData.businessPhoneNumber) {
      setFormData((prev) => ({
        ...prev,
        businessPhoneNumber: businessPhoneValue || "",
      }));
    }
  }, [businessPhoneValue]);

  const populateFormData = async () => {
    const combinedData = {
      ...registeredData?.user,
      ...registeredData?.profiles?.host,
    };
    setFormData({
      ...combinedData,
      profilePicUrl: combinedData.profilePicUrl?.fileDownloadUri ?? "",
      businessPicUrl: combinedData.businessPicUrl?.fileDownloadUri ?? "",
      driverLicenseFrontPicUrl:
        combinedData.driverLicenseFrontPicUrl?.fileDownloadUri ?? "",
      driverLicenseBackPicUrl:
        combinedData.driverLicenseBackPicUrl?.fileDownloadUri ?? "",
    });
    Object.keys(combinedData).forEach((key) => {
      let value = combinedData[key];
      if (
        [
          "profilePicUrl",
          "businessPicUrl",
          "driverLicenseFrontPicUrl",
          "driverLicenseBackPicUrl",
        ].includes(key)
      ) {
        value = combinedData[key]?.fileDownloadUri ?? "";
      }
      if (value === null || value === undefined) {
        console.warn(
          `Value for key "${key}" is null or undefined. Setting default value.`
        );
        value = "";
      }
      setValue(key as keyof HostProfileDTO, value);
    });
    // If a state value exists from the backend, update the city list accordingly.
    if (combinedData.state) {
      const selectedState = statesList.find(
        (state: any) => state.name === combinedData.state
      );
      if (selectedState && selectedState.cities) {
        setCityList(selectedState.cities.map((city: any) => city.name));
      }
    }
  };

  const handleChange = (event: any) => {
    const {
      target: { value, name },
    } = event;
    // Prevent updates for non-editable fields
    if (
      name === "firstName" ||
      name === "lastName" ||
      name === "dob" ||
      name === "businessName" ||
      name === "businessAddress" ||
      name === "state" ||
      name === "city" ||
      name === "zipCode"
    ) {
      return;
    }
    setValue(name, value, { shouldValidate: true });
    setFormData({ ...formData, [name]: value });

    if (name === "state") {
      const selectedState = statesList.find(
        (state: any) => state.name === value
      );
      if (selectedState && selectedState.cities) {
        setCityList(selectedState.cities.map((city: any) => city.name));
      } else {
        setCityList([]);
      }
      // Reset city when state changes
      setValue("city", "");
      setFormData((prev) => ({ ...prev, city: "" }));
    }
  };

  const breadcrumbLinks: BreadcrumbLink[] = [
    { title: "Home ", path: siteRoutes.home },
    { title: "Edit Profile", path: siteRoutes.editProfile },
  ];

  const onSubmit: SubmitHandler<HostProfileDTO> = async (data) => {
    if (activeTab === "personal") {
      const profilePic = getValues("profilePicUrl");
      if (!profilePic) {
        warningToaster("Please upload your profile picture");
        return;
      }
      const hasChanged = true;
      if (hasChanged) {
        const profilePic = getValues("profilePicUrl");
        if (typeof profilePic !== "string") {
          const form_data = new FormData();
          form_data.append("file", profilePic);
          const imageData: any = await uploadFile(form_data);
          console.log(imageData, "imageData");
          formData.profilePicId = imageData?.id;
        } else {
          console.log(registeredData, "registeredData");
          if (
            !formData.profilePicId &&
            registeredData?.user?.profilePicUrl?.id
          ) {
            formData.profilePicId = registeredData?.user?.profilePicUrl?.id;
          }
        }

        await updateProfile(formData);
        const updatedHostProfile = await getHostProfileData();
        setRegisteredData(updatedHostProfile);
        setTimeout(() => {
          navigate(siteRoutes.home);
        }, 700);
      }
    } else if (activeTab === "business") {
      const businessPic = getValues("businessPicUrl");
      if (!businessPic) {
        warningToaster("Please upload your business picture");
        return;
      }
      if (typeof businessPic !== "string") {
        const form_data = new FormData();
        form_data.append("file", businessPic);
        const imageData: any = await uploadFile(form_data);
        console.log(imageData, "imageData");
        formData.businessPicId = imageData?.id;
      } else {
        console.log(registeredData, "registeredData");
        if (
          !formData.businessPicId &&
          registeredData?.profiles?.host?.businessPicUrl?.id
        ) {
          formData.businessPicId =
            registeredData?.profiles?.host?.businessPicUrl?.id;
        }
      }
      const licenseFrontPic = getValues("driverLicenseFrontPicUrl");
      if (!licenseFrontPic) {
        warningToaster(
          "Please upload your driver's license / state ID front image"
        );
        return;
      }
      if (typeof licenseFrontPic !== "string") {
        const form_data = new FormData();
        form_data.append("file", licenseFrontPic);
        const imageData: any = await uploadFile(form_data);
        console.log(imageData, "imageData");
        formData.driverLicenseFrontPicId = imageData?.id;
      } else {
        console.log(registeredData, "registeredData");
        if (
          !formData.driverLicenseFrontPicId &&
          registeredData?.profiles?.host?.driverLicenseFrontPicUrl?.id
        ) {
          formData.driverLicenseFrontPicId =
            registeredData?.profiles?.host?.driverLicenseFrontPicUrl?.id;
          console.log(formData.businessPicId);
        }
      }

      const licenseBackPic = getValues("driverLicenseBackPicUrl");
      if (typeof licenseBackPic !== "string") {
        const form_data = new FormData();
        form_data.append("file", licenseBackPic);
        const imageData: any = await uploadFile(form_data);
        console.log(imageData, "imageData");
        formData.driverLicenseBackPicId = imageData?.id;
      } else {
        console.log(registeredData, "registeredData");
        if (
          !formData.driverLicenseBackPicId &&
          registeredData?.profiles?.host?.driverLicenseBackPicUrl?.id
        ) {
          formData.driverLicenseBackPicId =
            registeredData?.profiles?.host?.driverLicenseBackPicUrl?.id;
        }
      }
      await updateBusinessProfile(formData);
      const updatedHostProfile = await getHostProfileData();
      setRegisteredData(updatedHostProfile);
      setTimeout(() => {
        navigate(siteRoutes.home);
      }, 700);
    }
  };

  const uploadProfilePic = (event: any) => {
    const { files } = event.target;
    const file = files[0];
    setValue("profilePicUrl", file);
    setFormData({ ...formData, profilePicUrl: URL.createObjectURL(file) });
  };

  const uploadBusinessPic = (event: any) => {
    const { files } = event.target;
    const file = files[0];
    setValue("businessPicUrl", file);
    setFormData({ ...formData, businessPicUrl: URL.createObjectURL(file) });
  };

  const uploadLicenseFrontPic = (event: any) => {
    const { files } = event.target;
    const file = files[0];
    setValue("driverLicenseFrontPicUrl", file);
    setFormData({
      ...formData,
      driverLicenseFrontPicUrl: URL.createObjectURL(file),
    });
  };

  const uploadLicenseBackPic = (event: any) => {
    const { files } = event.target;
    const file = files[0];
    setValue("driverLicenseBackPicUrl", file);
    setFormData({
      ...formData,
      driverLicenseBackPicUrl: URL.createObjectURL(file),
    });
  };

  return (
    <div className="container">
      <Breadcrumb links={breadcrumbLinks} />
      <div className="flex flex-wrap justify-start my-4">
        <button
          type="button"
          className={`px-4 py-2 text-sm font-semibold ${
            activeTab === "personal"
              ? "text-lightBlueColor border-b-2 border-lightBlueColor"
              : "text-grayText"
          }`}
          onClick={() => setActiveTab("personal")}
        >
          Personal Details
        </button>
        <button
          type="button"
          className={`px-4 py-2 text-sm font-semibold ${
            activeTab === "business"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-gray-500"
          }`}
          onClick={() => setActiveTab("business")}
        >
          Business Details
        </button>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        {activeTab === "personal" ? (
          <Fragment>
            <div className="flex flex-col items-center w-[270px]">
              <div className="relative">
                <img
                  src={
                    formData.profilePicUrl
                      ? concatPathWithBackendUrl(formData.profilePicUrl)
                      : editProfileUserPc
                  }
                  alt="Profile"
                  className="w-36 h-36 rounded-full object-cover"
                />
                <label
                  htmlFor="profilePicUrl"
                  className="absolute bottom-2 right-0 bg-white shadow p-2 rounded-full cursor-pointer"
                >
                  <EditPic />
                </label>
                <input
                  id="profilePicUrl"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  {...register("profilePicUrl", { required: false })}
                  onChange={uploadProfilePic}
                />
              </div>
              {errors.profilePicUrl && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.profilePicUrl.message}
                </p>
              )}
              <div className="flex flex-col gap-2 items-center mt-3">
                <p className="text-base text-label px-5 font-semibold">
                  Upload Profile Picture
                </p>
                <p className="text-labelTextColor text-center text-sm">
                  For security purposes, only use updated/recent picture of host
                  by which attendees can identify you
                </p>
              </div>
            </div>
            <div className="w-full my-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="flex flex-col gap-1">
                <label className="custom-label">
                  First name
                </label>
                <input
                  type="text"
                  className="custom-input cursor-not-allowed"
                  {...register("firstName", {
                    required: "First name is required",
                  })}
                  value={formData.firstName}
                  onChange={handleChange}
                  disabled
                />
                {errors.firstName && (
                  <p className="text-red-500 text-sm">
                    {errors.firstName.message}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <label className="custom-label">
                  Last name
                </label>
                <input
                  type="text"
                  className="custom-input cursor-not-allowed"
                  {...register("lastName", {
                    required: "Last name is required",
                  })}
                  value={formData.lastName}
                  onChange={handleChange}
                  disabled
                />
                {errors.lastName && (
                  <p className="text-red-500 text-sm">
                    {errors.lastName.message}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <label className="custom-label">
                  Email
                </label>
                <input
                  type="email"
                  className="custom-input"
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: "Enter a valid email",
                    },
                  })}
                  value={formData.email}
                  onChange={handleChange}
                />
                {errors.email && (
                  <p className="text-red-500 text-sm">{errors.email.message}</p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <label className="custom-label">
                  Date of Birth
                </label>
                <input
                  type="date"
                  placeholder="Select Date"
                  className="custom-input cursor-not-allowed"
                  {...register("dob", {
                    required: "Date of Birth is required",
                  })}
                  value={formData.dob}
                  onChange={handleChange}
                  disabled
                />
                {errors.dob && (
                  <p className="text-red-500 text-sm">{errors.dob.message}</p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <label className="custom-label">
                  Gender
                </label>
                <select
                  className="custom-input"
                  {...register("gender", {
                    required: "Gender is required",
                  })}
                  value={formData.gender}
                  onChange={handleChange}
                >
                  <option value="">Select Gender</option>
                  {Object.values(GENDERS).map((gender, index) => {
                    return (
                      <option value={gender} key={index}>
                        {gender}
                      </option>
                    );
                  })}
                </select>
                {errors.gender && (
                  <p className="text-red-500 text-sm">
                    {errors.gender.message}
                  </p>
                )}
              </div>
              <div className="flex flex-col">
                <PhoneNumberInput
                  control={control}
                  name="phone"
                  label="Phone Number"
                  error={errors.phone}
                  placeholder="(123) 456-7890"
                  required={true}
                  className="custom-input"
                  labelClassName="custom-label"
                  gap="gap-1"
                />
              </div>
            </div>
          </Fragment>
        ) : (
          <Fragment>
            <div className="flex flex-col ">
              <div className="relative  w-full">
                {isBusinessPicLoading && (
                  <div className="absolute h-[330px] inset-0 flex items-center justify-center bg-white z-10 ">
                    {/* Replace with your actual loading component/spinner */}
                    <div className="loader ">
                      <Loader />
                    </div>
                  </div>
                )}
                <img
                  src={
                    formData.businessPicUrl
                      ? concatPathWithBackendUrl(formData.businessPicUrl)
                      : businsessProfileBg
                  }
                  alt={formData.businessPicUrl}
                  className="w-full h-[300px] rounded-xl object-center"
                />
                <label
                  htmlFor="businessPicUrl"
                  className="absolute bottom-[-20px] right-16 bg-white shadow p-2 rounded-full cursor-pointer"
                >
                  <EditPic />
                </label>
                <input
                  id="businessPicUrl"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  {...register("businessPicUrl", { required: false })}
                  onChange={uploadBusinessPic}
                />
              </div>
              {errors.businessPicUrl && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.businessPicUrl.message}
                </p>
              )}
              <div className="flex flex-col gap-2 items-start w-full md:w-1/2 mt-3">
                <p className="text-base text-label font-semibold">
                  Upload Business Picture
                </p>
                <p className="text-labelTextColor text-start text-sm">
                  For security purposes, only use an updated/recent picture of
                  the business by which the attendee can easily identify the
                  business by when arriving to the location. External storefront
                  is recommended. A business logo is not acceptable.
                </p>
              </div>
            </div>
            <div className="w-full my-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="flex flex-col gap-1">
                <label className="custom-label">
                  Business Name
                </label>
                <input
                  type="text"
                  className="custom-input cursor-not-allowed"
                  {...register("businessName", {
                    required: "Business Name is required",
                  })}
                  value={formData.businessName}
                  onChange={handleChange}
                  disabled
                />
                {errors.businessName && (
                  <p className="text-red-500 text-sm">
                    {errors.businessName.message}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <label className="custom-label">
                  Business Email
                </label>
                <input
                  type="email"
                  className="custom-input"
                  {...register("businessEmail", {
                    required: "Email is required",
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: "Enter a valid email",
                    },
                  })}
                  value={formData.businessEmail}
                  onChange={handleChange}
                />
                {errors.businessEmail && (
                  <p className="text-red-500 text-sm">
                    {errors.businessEmail.message}
                  </p>
                )}
              </div>
              <div className="flex flex-col">
                <PhoneNumberInput
                  control={control}
                  name="businessPhoneNumber"
                  label="Business Phone Number"
                  error={errors.businessPhoneNumber}
                  placeholder="(123) 456-7890"
                  required={true}
                  className="custom-input"
                  labelClassName="custom-label"
                  gap="gap-1"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="custom-label">
                  Business Website
                </label>
                <input
                  type="text"
                  className="custom-input"
                  {...register("businessWebsite", {
                    required: false,
                  })}
                  value={formData.businessWebsite}
                  onChange={handleChange}
                />
                {errors.businessWebsite && (
                  <p className="text-red-500 text-sm">
                    {errors.businessWebsite.message}
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="flex flex-col gap-1 relative">
                <label className="custom-label">
                  Mission
                </label>
                <div className="relative flex items-center">
                  <input
                    type="text"
                    className="custom-input"
                    {...register("missionStatement", {
                      required: "Business Mission is required",
                    })}
                    value={formData.missionStatement}
                    onChange={handleChange}
                  />
                  <div
                    className="absolute right-2 cursor-pointer"
                    onMouseEnter={() => setIsMissionTooltipVisible(true)}
                    onMouseLeave={() => setIsMissionTooltipVisible(false)}
                  >
                    <InforCircle />
                  </div>
                  {isMissionTooltipVisible && (
                    <div className="absolute w-[230px] h-fit top-[-100px] right-3 p-4 rounded-t-lg rounded-bl-lg bg-primaryBlueBg text-white text-xs opacity-100 transition-opacity duration-300 z-10">
                      Tell us how your business and/or educational experiences
                      will enhance the community. This is your opportunity to
                      connect with the community through shared values.
                    </div>
                  )}
                </div>
                {errors.missionStatement && (
                  <p className="text-red-500 text-sm">
                    {errors.missionStatement.message}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1 relative">
                <label className="custom-label">
                  About
                </label>
                <div className="relative flex items-center">
                  <input
                    className="custom-input"
                    {...register("about", {
                      required: "About Business is required",
                    })}
                    value={formData.about}
                    onChange={handleChange}
                  />
                  <div
                    className="absolute right-2 cursor-pointer"
                    onMouseEnter={() => setIsAboutTooltipVisible(true)}
                    onMouseLeave={() => setIsAboutTooltipVisible(false)}
                  >
                    <InforCircle />
                  </div>
                  {isAboutTooltipVisible && (
                    <div className="absolute w-[230px] h-fit top-[-100px] right-3 p-4 rounded-t-lg rounded-bl-lg bg-primaryBlueBg text-white text-xs opacity-100 transition-opacity duration-300 z-10">
                      Tell your audience some fun or unique details about your
                      business. Include anything parents/kids would enjoy
                      learning about which may contribute to a stronger
                      connection.
                    </div>
                  )}
                </div>
                {errors.about && (
                  <p className="text-red-500 text-sm">{errors.about.message}</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 my-4">
              <div className="flex flex-col gap-1">
                <label className="custom-label">
                  Industry
                </label>
                <select
                  {...register("industry", {
                    required: "Industry is required",
                  })}
                  value={formData.industry}
                  onChange={handleChange}
                  className="custom-input"
                >
                  <option className="h-10" value="">
                    Select
                  </option>
                  {Object.values(INDUSTRY_TYPES).map((industry) => (
                    <option
                      key={industry}
                      value={industry}
                      className="h-[40px]"
                    >
                      {industry.replace(/_/g, " ")}
                    </option>
                  ))}
                </select>
                {errors.industry && (
                  <p className="text-red-500 text-sm">
                    {errors.industry.message}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <label className="custom-label">
                  Address
                </label>
                <input
                  type="text"
                  className="custom-input cursor-not-allowed"
                  {...register("businessAddress", {
                    required: "Address is required",
                  })}
                  value={formData.businessAddress}
                  onChange={handleChange}
                  disabled
                />
                {errors.businessAddress && (
                  <p className="text-red-500 text-sm">
                    {errors.businessAddress.message}
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
              {/* State Dropdown */}
              <div className="flex flex-col gap-1">
                <label className="custom-label">
                  State
                </label>
                <select
                  // name="state"
                  className="custom-input cursor-not-allowed"
                  {...register("state", { required: "State is required" })}
                  value={formData.state}
                  onChange={handleChange}
                  disabled
                >
                  <option value="" disabled>
                    Select
                  </option>
                  {statesList.map((state: any) => (
                    <option key={state.id} value={state.name}>
                      {state.name}
                    </option>
                  ))}
                </select>
                {errors.state && (
                  <p className="text-red-500 text-sm">{errors.state.message}</p>
                )}
              </div>

              {/* City Dropdown */}
              <div className="flex flex-col gap-1">
                <label className="custom-label">
                  City
                </label>
                <select
                  // name="city"
                  className="custom-input cursor-not-allowed"
                  {...register("city", { required: "City is required" })}
                  value={formData.city}
                  onChange={handleChange}
                  disabled
                >
                  <option value="" disabled>
                    Select
                  </option>
                  {cityList.map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                </select>
                {errors.city && (
                  <p className="text-red-500 text-sm">{errors.city.message}</p>
                )}
              </div>

              {/* Zip Code Input */}
              <div className="flex flex-col gap-1">
                <label className="custom-label">
                  Zip Code
                </label>
                <input
                  type="text"
                  className="custom-input cursor-not-allowed"
                  {...register("zipCode", {
                    required: "Zip code is required",
                    pattern: {
                      value: /^[0-9]{5}(?:-[0-9]{4})?$/,
                      message: "Please enter a valid ZIP code",
                    },
                  })}
                  value={formData.zipCode}
                  onChange={handleChange}
                  disabled
                />
                {errors.zipCode && (
                  <p className="text-red-500 text-sm">
                    {errors.zipCode.message}
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 my-4">
              <div className="flex flex-col gap-1">
                <p className="custom-label">
                  For security purposes please upload a copy of a valid ID or
                  Driver's License
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-6">
              {/* Front Side Upload */}
              <div>
                <label className="custom-label">
                  Front Side ID
                </label>
                <label className="flex items-center justify-start cursor-pointer mt-3">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={uploadLicenseFrontPic}
                  />
                  <span className="w-[180px] h-[120px] flex items-center justify-center border border-dashed border-gray-300">
                    {formData.driverLicenseFrontPicUrl ? (
                      <img
                        className="w-full h-full object-contain"
                        src={
                          formData.driverLicenseFrontPicUrl
                            ? concatPathWithBackendUrl(
                                formData.driverLicenseFrontPicUrl
                              )
                            : ""
                        }
                        alt="Front ID"
                      />
                    ) : (
                      <span className="text-gray-400">Upload Front Image</span>
                    )}
                  </span>
                </label>
              </div>

              {/* Back Side Upload (Optional) */}
              <div>
                <label className="custom-label mt-6">
                  Back Side ID
                </label>
                <label className="flex items-center justify-start cursor-pointer mt-3 relative">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={uploadLicenseBackPic}
                  />
                  <span className="w-[180px] h-[120px] flex items-center justify-center border border-dashed border-gray-300 relative">
                    {formData.driverLicenseBackPicUrl ? (
                      <>
                        <img
                          className="w-full h-full object-contain"
                          src={
                            formData.driverLicenseBackPicUrl
                              ? concatPathWithBackendUrl(
                                  formData.driverLicenseBackPicUrl
                                )
                              : ""
                          }
                          alt="Back ID"
                        />
                      </>
                    ) : (
                      <span className="text-gray-400">Upload Back Image</span>
                    )}
                  </span>
                </label>
              </div>
            </div>
          </Fragment>
        )}
        <button
          type="submit"
          className="px-4 py-2 bg-primaryBlueBg text-white rounded-full mt-4 md:mt-8"
        >
          Update
        </button>
      </form>
    </div>
  );
};

export default EditProfile;
